/* eslint-disable @woocommerce/dependency-group */
/* global finix_google_pay_params */

import { __ } from '@wordpress/i18n';

export default class FinixGooglePay {
	static paymentsClient = null;

	static baseRequest = {
		apiVersion: 2,
		apiVersionMinor: 0,
	};

	static environment = finix_google_pay_params.environment === 'sandbox' ? 'TEST' : 'PRODUCTION';

	static allowedCardNetworks = [ 'AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA' ];

	static allowedCardAuthMethods = [ 'PAN_ONLY', 'CRYPTOGRAM_3DS' ];

	static tokenizationSpecification = {
		type: 'PAYMENT_GATEWAY',
		parameters: {
			gateway: 'finix',
			gatewayMerchantId: finix_google_pay_params.merchant_identity,
		},
	};

	static baseCardPaymentMethod = {
		type: 'CARD',
		parameters: {
			allowedAuthMethods: FinixGooglePay.allowedCardAuthMethods,
			allowedCardNetworks: FinixGooglePay.allowedCardNetworks,
		},
	};

	static cardPaymentMethod = {
		...FinixGooglePay.baseCardPaymentMethod,
		tokenizationSpecification: FinixGooglePay.tokenizationSpecification,
	};

	static getPaymentsClient() {
		if ( ! FinixGooglePay.paymentsClient ) {
			FinixGooglePay.paymentsClient = new google.payments.api.PaymentsClient( { environment: FinixGooglePay.environment } );
		}
		return FinixGooglePay.paymentsClient;
	}

	static getIsReadyToPayRequest() {
		return {
			...FinixGooglePay.baseRequest,
			allowedPaymentMethods: [ FinixGooglePay.baseCardPaymentMethod ],
		};
	}

	static getTransactionInfo() {
		let lineItems = [];

		// Add product line items if value is not zero.
		Object.values( finix_google_pay_params.products ).forEach( ( product ) => {
			if ( parseFloat( product.total ) !== 0 ) {
				lineItems.push( {
					label: product.quantity > 1 ? `${ product.name } x${ product.quantity }` : product.name,
					type: 'LINE_ITEM',
					price: product.total,
				} );
			}
		} );

		lineItems.push( {
			label: __( 'Subtotal', 'finix-for-woocommerce' ),
			type: 'SUBTOTAL',
			price: finix_google_pay_params.subtotal || finix_google_pay_params.amount,
		} );

		// Add any offer details to the transaction info.
		lineItems.push(
			...finix_google_pay_params.coupons.map( ( offer ) => ( {
				label: __( 'Discount code:', 'finix-for-woocommerce' ) + ' ' + offer.code,
				type: 'DISCOUNT',
				price: ( -1 * parseFloat( offer.total ).toFixed( 2 ) ).toString(), // Ensure negative value for discounts.
			} ) )
		);

		// Add Shipping if is not Zero.
		if ( finix_google_pay_params.shipping_amount > 0 ) {
			lineItems.push( {
				label: __( 'Shipping', 'finix-for-woocommerce' ),
				type: 'SHIPPING_OPTION',
				price: finix_google_pay_params.shipping_amount || '0.00',
			} );
		}

		// Add Tax if is not Zero.
		if ( finix_google_pay_params.tax_amount > 0 ) {
			lineItems.push( {
				label: __( 'Tax', 'finix-for-woocommerce' ),
				type: 'TAX',
				price: finix_google_pay_params.tax_amount || '0.00',
			} );
		}

		return {
			countryCode: finix_google_pay_params.merchant_country,
			currencyCode: finix_google_pay_params.currency_code,
			totalPriceStatus: 'FINAL',
			totalPriceLabel: __( 'Total', 'finix-for-woocommerce' ),
			totalPrice: finix_google_pay_params.amount,
			displayItems: lineItems,
		};
	}

	static getPaymentDataRequest() {
		return {
			...FinixGooglePay.baseRequest,
			allowedPaymentMethods: [ FinixGooglePay.cardPaymentMethod ],
			transactionInfo: FinixGooglePay.getTransactionInfo(),
			merchantInfo: {
				merchantId: finix_google_pay_params.google_merchant_id,
				merchantName: finix_google_pay_params.merchant_name,
			},
		};
	}

	static prefetchPaymentData() {
		FinixGooglePay.getPaymentsClient().prefetchPaymentData( FinixGooglePay.getPaymentDataRequest() );
	}

	static processPayment = async () => {
		const errorText = __( 'There was an error while processing the payment using Google Pay.', 'finix-for-woocommerce' );

		try {
			const paymentData = await FinixGooglePay.getPaymentsClient().loadPaymentData( FinixGooglePay.getPaymentDataRequest() );

			const token = paymentData.paymentMethodData.tokenizationData.token;
			if ( ! token ) {
				throw new Error( errorText );
			}

			const requestBody = {
				provider: 'GOOGLE_PAY',
				payment_token: token,
				process_payment: true,
				merchant_identity: finix_google_pay_params.merchant_identity,
				wp_nonce: finix_google_pay_params.nonce,
				billing_info: finix_google_pay_params.billing_data,
			};

			const response = await fetch( finix_google_pay_params.url.webhook, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( requestBody ),
			} ).then( ( r ) => r.json() );

			if ( response.status === 201 ) {
				document.getElementById( 'finix_google_pay_success' ).value = true;
				document.getElementById( 'finix_google_pay_transaction_id' ).value = response.response.id;
				return true;
			}

			document.getElementById( 'finix_google_pay_success' ).value = false;
			document.getElementById( 'finix_google_pay_transaction_id' ).value = '';
			throw new Error( errorText );
		} catch ( err ) {
			throw new Error( err.message || errorText );
		}
	};

	static triggerPayment() {
		const btn = document.querySelector( '.wc-block-components-checkout-place-order-button' );
		if ( btn ) btn.click();
	}
}
